/**
 * CompactStorefront — v149.69, v149.70.
 *
 * The "Compact" layout of `/store/{slug}`, for shops with hundreds of
 * products, services and events: small tiles (three across on a phone, up
 * to six on desktop) with the photo — or the item's initials — an ADD
 * button, the price (and the MRP struck through) and the name. The seller
 * picks it in Commerce → Storefront (`storefront_settings.layout`).
 *
 * Nothing is filtered or paged in the browser. Search, category, type and
 * sort all go to `GET /public/storefront/{slug}` and come back one page of
 * 60 at a time; scrolling asks for the next page. The page's first answer
 * (`initial`, fetched by `Storefront.jsx` with `auto=1`) is page 1 and
 * also carries the shop's categories and how many products / services /
 * events it has — that is what the filters are built from.
 *
 * Also here: the search box and category strip stay pinned at the top
 * while scrolling, the cart bar stays pinned at the bottom, and there is
 * ONE product-details sheet and ONE buy modal for the whole grid.
 *
 * Buying is the classic layout's: products go through the shared cart
 * (`StorefrontCartContext` → `StorefrontCartBar` → `CartCheckoutModal`),
 * services and tickets open `StorefrontBuyModal`, external links open,
 * and `productSaleState` decides what a product allows.
 */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { ShoppingBag, Search, X, Loader2, Trophy, ExternalLink, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { openExternalUrl } from "../../lib/openExternal";
import { useAuth } from "../../context/AuthContext";
import { useStorefrontCart } from "./StorefrontCartContext";
import StorefrontCartBar from "./StorefrontCartBar";
import { StorefrontBuyModal } from "./StorefrontBuyModal";
import ProductThumb from "./ProductThumb";
import { ProductInfoSheet } from "../actionBlocks/commerce";
import { trackStorefront } from "./_shared";
import { getProductSaleState, formatShortDate } from "./productSaleState";

export const STORE_PAGE_SIZE = 60;     // items per request; divides by 3, 4, 5 and 6 columns

const KIND_LABELS = { product: "Products", service: "Services", ticket: "Events" };
const SORTS = [
  ["newest", "Newest first"],
  ["price_asc", "Price: low–high"],
  ["price_desc", "Price: high–low"],
  ["name", "Name: A–Z"],
];
const NO_FILTERS = { q: "", category: "", kind: "", sort: "newest" };

const money = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v)
    ? v.toLocaleString("en-IN")
    : v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// What a tile's button does, from the shared sale rules.
function tileAction(product) {
  const s = getProductSaleState(product);
  if (s.isTicketSoldOut) return { ...s, mode: "blocked", label: "Sold out" };
  if (s.availBlocked) {
    const ended = s.availStatus === "ended";
    if (!product.buy_url && !s.canBuyNow) return { ...s, mode: "none" };
    return { ...s, mode: "blocked", label: ended ? (s.isService ? "Closed" : "Sold out") : "Soon" };
  }
  if (product.buy_url) return { ...s, mode: "external", label: s.isService ? "Book" : "Buy" };
  if (s.canBuyNow && (s.isService || s.isTicket)) return { ...s, mode: "direct", label: s.isTicket ? "Tickets" : "Book" };
  if (s.canBuyNow) return { ...s, mode: "cart", label: "Add" };
  return { ...s, mode: "none" };
}

async function fetchPage(slug, page, f) {
  const params = new URLSearchParams({ page: String(page), limit: String(STORE_PAGE_SIZE) });
  if (f.q) params.set("q", f.q);
  if (f.category) params.set("category", f.category);
  if (f.kind) params.set("kind", f.kind);
  if (f.sort && f.sort !== "newest") params.set("sort", f.sort);
  const r = await fetch(`${API}/public/storefront/${encodeURIComponent(slug)}?${params}`);
  if (!r.ok) throw new Error("Could not load products");
  return r.json();
}

const sameFilters = (a, b) => a.q === b.q && a.category === b.category && a.kind === b.kind && a.sort === b.sort;

export default function CompactStorefront({ slug, initial, settings }) {
  const cart = useStorefrontCart();
  const { user } = useAuth();
  // `initial` is page 1 with no filters — when the server answered a page.
  const initialIsPage = initial?.page === 1 && initial?.limit === STORE_PAGE_SIZE;

  const [items, setItems] = useState(initialIsPage ? initial.products : []);
  const [count, setCount] = useState(initialIsPage ? (initial.total ?? initial.products.length) : 0);
  const [categories, setCategories] = useState(initial?.categories || []);
  const [kinds, setKinds] = useState(initial?.kinds || null);
  const [totalAll, setTotalAll] = useState(initial?.total_all ?? initial?.total ?? 0);
  const [query, setQuery] = useState("");                 // what's typed
  const [filters, setFilters] = useState(NO_FILTERS);     // what's applied
  const [busy, setBusy] = useState(!initialIsPage);
  const [loadError, setLoadError] = useState(false);
  const [infoProduct, setInfoProduct] = useState(null);
  const [buyProduct, setBuyProduct] = useState(null);
  const request = useRef(0);
  const sentinel = useRef(null);
  const firstRun = useRef(true);

  // The search box and category strip, and the cart, are pinned with
  // `fixed`, not `sticky`: `#root` clips sideways overflow, which makes it
  // the sticky container, and it never scrolls. The bar pins once its
  // place in the page has scrolled off the top (the slot keeps the height,
  // so nothing jumps).
  const searchSlot = useRef(null);
  const [searchPinned, setSearchPinned] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const el = searchSlot.current;
      if (el) setSearchPinned(el.getBoundingClientRect().top < 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Typing applies a beat after the last key.
  useEffect(() => {
    const term = query.trim();
    if (term === filters.q) return undefined;
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: term })), term ? 300 : 0);
    return () => clearTimeout(t);
  }, [query, filters.q]);

  // Any filter change: page 1 of the new view, from the server.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      if (initialIsPage) return;                       // already showing page 1
    } else if (initialIsPage && sameFilters(filters, NO_FILTERS)) {
      // Back to the unfiltered store: the first answer is still good.
      request.current += 1;
      setItems(initial.products);
      setCount(initial.total ?? initial.products.length);
      setBusy(false);
      setLoadError(false);
      return;
    }
    const id = ++request.current;
    setBusy(true);
    setLoadError(false);
    fetchPage(slug, 1, filters).then((d) => {
      if (id !== request.current) return;
      setItems(d.products || []);
      setCount(d.total ?? (d.products || []).length);
      if (Array.isArray(d.categories)) setCategories(d.categories);
      if (d.kinds) setKinds(d.kinds);
      if (d.total_all != null) setTotalAll(d.total_all);
      // Scrolled deep with the bar pinned: bring the new results to the top.
      const slot = searchSlot.current;
      if (slot && slot.getBoundingClientRect().top < 0) slot.scrollIntoView({ block: "start" });
    }).catch(() => {
      if (id !== request.current) return;
      setLoadError(true);
      toast.error("Couldn't load products — try again.");
    }).finally(() => {
      if (id === request.current) setBusy(false);
    });
  // `initial` is the page's first answer and never changes for a slug.
  }, [filters, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMore = items.length < count;
  const more = useCallback(async () => {
    if (busy || items.length >= count) return;
    const id = ++request.current;
    setBusy(true);
    setLoadError(false);
    try {
      const d = await fetchPage(slug, Math.floor(items.length / STORE_PAGE_SIZE) + 1, filters);
      if (id !== request.current) return;
      const have = new Set(items.map((p) => p.product_id));
      const fresh = (d.products || []).filter((p) => !have.has(p.product_id));
      setItems((prev) => [...prev, ...fresh]);
      // Nothing new came back: stop asking (the catalog changed under us).
      setCount(fresh.length ? (d.total ?? count) : items.length);
    } catch {
      if (id === request.current) setLoadError(true);
    } finally {
      if (id === request.current) setBusy(false);
    }
  }, [items, count, busy, slug, filters]);

  // Ask for the next page as the end of the grid comes near.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || loadError || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) more();
    }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadError, more]);

  // Through a ref, so these stay the same functions when the cart changes
  // and only the tile whose quantity moved is redrawn.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const add = useCallback((product) => {
    try { trackStorefront(slug, "add_to_cart", product.product_id); } catch { /* noop */ }
    cartRef.current?.add(product);
  }, [slug]);
  const setUnits = useCallback((productId, units) => cartRef.current?.setUnits(productId, units), []);
  const openBuy = useCallback((product) => {
    trackStorefront(slug, "product_click", product.product_id);
    trackStorefront(slug, "buy_click", product.product_id);
    setBuyProduct(product);
  }, [slug]);
  const openExternal = useCallback((product) => {
    trackStorefront(slug, "product_click", product.product_id);
    trackStorefront(slug, "buy_click", product.product_id);
    // New tab on the web; the in-app browser sheet in the native app
    // (where a bare `window.open` would replace the app itself).
    openExternalUrl(product.buy_url);
  }, [slug]);

  const cartCount = cart?.count || 0;
  const filtered = !sameFilters(filters, NO_FILTERS);
  const clearAll = () => { setQuery(""); setFilters(NO_FILTERS); };
  const kindKeys = kinds ? Object.keys(KIND_LABELS).filter((k) => kinds[k] > 0) : [];
  const activeCategory = categories.find((c) => c.category_id === filters.category);
  const noun = kindKeys.length === 1 && kindKeys[0] !== "product"
    ? KIND_LABELS[kindKeys[0]].toLowerCase().replace(/s$/, "")
    : kindKeys.length > 1 ? "item" : "product";
  const plural = (n) => `${n} ${noun}${n === 1 ? "" : "s"}`;

  // The details sheet's button does what the tile's button does.
  const info = infoProduct ? tileAction(infoProduct) : null;
  const infoCTA = !info ? undefined
    : info.mode === "cart" ? () => { add(infoProduct); setInfoProduct(null); toast.success("Added to cart"); }
    : info.mode === "direct" ? () => { const p = infoProduct; setInfoProduct(null); openBuy(p); }
    : info.mode === "external" ? () => { const p = infoProduct; setInfoProduct(null); openExternal(p); }
    : undefined;
  const infoLabel = !info ? undefined
    : info.mode === "blocked" ? (info.isTicketSoldOut ? "Sold out" : info.availStatus === "ended" ? "Sales closed" : `Opens ${formatShortDate(info.winFrom)}`)
    : info.mode === "cart" ? `Add to cart — ${info.sym}${money(infoProduct.price)}`
    : info.mode === "direct" ? (info.isTicket ? "Get tickets" : `Book Now — ${info.sym}${money(infoProduct.price)}`)
    : info.mode === "external" ? (info.isService ? "Book Now" : "Buy Now")
    : undefined;

  const chip = (on) => `flex-shrink-0 h-9 px-3 border-2 border-ink text-xs font-bold whitespace-nowrap ${on ? "bg-ink text-white" : "bg-white text-ink"}`;
  const pinPad = searchPinned ? "max-w-5xl mx-auto px-3 sm:px-6" : "";

  return (
    <div data-testid="compact-store">
      <div
        ref={searchSlot}
        className={categories.length > 0 ? "h-[6.25rem]" : "h-[3.25rem]"}
        data-testid="compact-store-search-slot"
      >
        <div
          className={searchPinned ? "fixed top-0 inset-x-0 z-20 bg-cream border-b-2 border-ink/10" : ""}
          style={searchPinned ? { paddingTop: "env(safe-area-inset-top, 0px)" } : undefined}
          data-pinned={searchPinned ? "true" : "false"}
          data-testid="compact-store-search-bar"
        >
          <label className={`relative block py-1.5 ${pinPad}`}>
            <span className="sr-only">Search the store</span>
            <Search className={`w-4 h-4 absolute top-1/2 -translate-y-1/2 text-ink/50 ${searchPinned ? "left-[1.375rem] sm:left-[2.125rem]" : "left-2.5"}`} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${plural(totalAll)}`}
              enterKeyHint="search"
              data-testid="compact-store-search"
              className="w-full h-10 border-2 border-ink bg-white pl-8 pr-9 text-sm [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                data-testid="compact-store-search-clear"
                className={`absolute top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center ${searchPinned ? "right-4 sm:right-7" : "right-1"}`}
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
            )}
          </label>
          {categories.length > 0 && (
            // Categories scroll sideways inside their own strip; the page never does.
            <div
              className={`flex items-center gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pb-1.5 ${pinPad}`}
              role="tablist"
              aria-label="Categories"
              data-testid="compact-store-categories"
            >
              <button type="button" role="tab" aria-selected={!filters.category}
                onClick={() => setFilters((f) => ({ ...f, category: "" }))}
                data-testid="compact-store-category-all" className={chip(!filters.category)}>
                All
              </button>
              {categories.map((c) => (
                <button key={c.category_id} type="button" role="tab" aria-selected={filters.category === c.category_id}
                  onClick={() => setFilters((f) => ({ ...f, category: f.category === c.category_id ? "" : c.category_id }))}
                  data-testid={`compact-store-category-${c.category_id}`} className={chip(filters.category === c.category_id)}>
                  {c.name} <span className="font-normal opacity-70">{c.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* One row: how many, the type filter (only when the shop sells more
          than one type) and the sort. */}
      <div className="flex items-center gap-2 mt-2 mb-2 min-w-0" data-testid="compact-store-toolbar">
        <p className="flex-1 min-w-0 text-[11px] leading-tight text-ink/60" data-testid="compact-store-count" aria-live="polite">
          {busy && items.length === 0 ? "Loading…"
            : filters.q ? `${count} result${count === 1 ? "" : "s"} for “${filters.q}”${activeCategory ? ` in ${activeCategory.name}` : ""}`
            : activeCategory ? `${plural(count)} in ${activeCategory.name}`
            : filters.kind ? `${count} ${count === 1 ? KIND_LABELS[filters.kind].toLowerCase().replace(/s$/, "") : KIND_LABELS[filters.kind].toLowerCase()}`
            : plural(count)}
        </p>
        {kindKeys.length > 1 && (
          <label className="flex-shrink-0">
            <span className="sr-only">Type</span>
            <select
              value={filters.kind}
              onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
              data-testid="compact-store-kind"
              className="h-9 w-[6.75rem] border-2 border-ink bg-white px-1.5 text-xs font-bold"
            >
              <option value="">All types</option>
              {kindKeys.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select>
          </label>
        )}
        <label className="flex-shrink-0">
          <span className="sr-only">Sort by</span>
          <select
            value={filters.sort}
            onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
            data-testid="compact-store-sort"
            className="h-9 w-[8.25rem] border-2 border-ink bg-white px-1.5 text-xs font-bold"
          >
            {SORTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </label>
      </div>

      {items.length === 0 && !busy ? (
        <div className="border-2 border-dashed border-ink/40 bg-white p-8 text-center" data-testid="compact-store-no-results">
          <ShoppingBag className="w-8 h-8 mx-auto text-ink/40" />
          <p className="mt-2 font-bold">
            {loadError ? "Couldn't load the store."
              : filters.q ? `Nothing matches “${filters.q}”.`
              : "Nothing here yet."}
          </p>
          {loadError ? (
            <button type="button" onClick={() => setFilters((f) => ({ ...f }))} data-testid="compact-store-reload"
              className="mt-3 border-2 border-ink bg-white px-3 py-1.5 font-bold text-xs shadow-brutalSm">
              Try again
            </button>
          ) : filtered && (
            <button type="button" onClick={clearAll} data-testid="compact-store-clear-filters"
              className="mt-3 border-2 border-ink bg-white px-3 py-1.5 font-bold text-xs shadow-brutalSm">
              Show everything
            </button>
          )}
        </div>
      ) : (
        <ul
          className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-x-2 gap-y-4 sm:gap-x-3 ${busy && items.length > 0 && !hasMore ? "opacity-60" : ""}`}
          data-testid="compact-store-grid"
        >
          {items.map((p) => (
            <CompactTile
              key={p.product_id}
              product={p}
              units={cart?.items?.[p.product_id]?.units || 0}
              onOpen={setInfoProduct}
              onAdd={add}
              onUnits={setUnits}
              onBuy={openBuy}
              onExternal={openExternal}
            />
          ))}
        </ul>
      )}

      {hasMore && items.length > 0 && (
        <div ref={sentinel} className="py-4 text-center" data-testid="compact-store-more">
          {loadError ? (
            <button
              type="button"
              onClick={more}
              data-testid="compact-store-retry"
              className="border-2 border-ink bg-white px-3 py-1.5 font-bold text-xs shadow-brutalSm"
            >
              Couldn&apos;t load more — try again
            </button>
          ) : (
            <button
              type="button"
              onClick={more}
              disabled={busy}
              data-testid="compact-store-load-more"
              className="border-2 border-ink bg-white px-3 py-1.5 font-bold text-xs shadow-brutalSm inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              {busy ? "Loading…" : `Show more (${items.length} of ${count})`}
            </button>
          )}
        </div>
      )}

      {/* The cart stays within reach however long the list is: pinned to
          the bottom of the screen, with room left under the last row. */}
      {cartCount > 0 && <div className="h-28" aria-hidden="true" data-testid="compact-store-cart-spacer" />}
      <div
        className={cartCount > 0 ? "fixed bottom-0 inset-x-0 z-20 bg-cream border-t-2 border-ink/10" : ""}
        style={cartCount > 0 ? { paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" } : undefined}
        data-testid="compact-store-cart-dock"
      >
        <div className={cartCount > 0 ? "max-w-5xl mx-auto px-3 sm:px-6 -mt-2" : ""}>
          <StorefrontCartBar
            slug={slug}
            buyerUser={null}
            requirePhone={!!settings?.cart_require_phone}
            requireAddress={!!settings?.cart_require_address}
            deliveryCharge={Number(settings?.cart_delivery_charge) || 0}
            codEnabled={!!settings?.cod_enabled}
            onlineEnabled={settings?.online_enabled !== false}
          />
        </div>
      </div>

      <ProductInfoSheet
        open={!!infoProduct}
        onClose={() => setInfoProduct(null)}
        product={infoProduct}
        onCTA={infoCTA}
        ctaLabel={infoLabel}
        ctaTestId="compact-store-info-cta"
        ctaDisabled={info?.mode === "blocked"}
      />
      {buyProduct && (
        <StorefrontBuyModal
          product={buyProduct}
          slug={slug}
          requireOtp={!!settings?.require_phone_otp}
          otpChannel={settings?.otp_channel || "auto"}
          codEnabled={!!settings?.cod_enabled}
          onlineEnabled={settings?.online_enabled !== false}
          buyerUser={user}
          onClose={() => setBuyProduct(null)}
          onSuccess={() => {
            toast.success(String(buyProduct.kind || "").toLowerCase() === "service"
              ? "Booking confirmed — the seller will get in touch."
              : "Payment received — the seller will get in touch.");
            trackStorefront(slug, "purchase", buyProduct.product_id);
            setBuyProduct(null);
          }}
        />
      )}
    </div>
  );
}

// One small tile. Memoised: adding to the cart redraws only that tile.
const CompactTile = memo(function CompactTile({ product, units, onOpen, onAdd, onUnits, onBuy, onExternal }) {
  const id = product.product_id;
  const a = tileAction(product);
  const img = (product.image_urls && product.image_urls[0]) || product.image_url || null;
  const mrp = Number(product.mrp) || 0;
  const price = Number(product.price) || 0;
  const tiers = a.isTicket && Array.isArray(product.ticket_tiers) && product.ticket_tiers.length > 1
    ? Math.min(...product.ticket_tiers.map((t) => Number(t.price || 0)))
    : null;
  const note = a.isTicketSoldOut ? "Sold out"
    : a.availLabel ? a.availLabel
    : a.isTicket && product.event_date_from ? formatShortDate(String(product.event_date_from).slice(0, 10))
    : a.isService ? "Service"
    : "";

  return (
    <li className="min-w-0 flex flex-col" data-testid={`compact-tile-${id}`}>
      <div className="relative aspect-square border-2 border-ink bg-white">
        <button
          type="button"
          onClick={() => onOpen(product)}
          aria-label={`View details for ${product.title}`}
          data-testid={`compact-tile-open-${id}`}
          className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden"
        >
          {/* v149.70 — No photo (or a broken one): the item's initials. */}
          <ProductThumb
            src={img}
            name={product.title}
            width={320}
            fit="contain"
            className="w-full h-full"
            testid={`compact-tile-img-${id}`}
            fallbackTestid={`compact-tile-initials-${id}`}
          />
        </button>
        {product.is_best_seller && (
          <span
            className="absolute top-0 left-0 border-r-2 border-b-2 border-ink bg-sun p-0.5"
            title="Best seller"
            data-testid={`compact-tile-bestseller-${id}`}
          >
            <Trophy className="w-3 h-3" strokeWidth={2.5} />
            <span className="sr-only">Best seller</span>
          </span>
        )}
        {a.mode === "cart" && units > 0 ? (
          <div
            className="absolute bottom-1 inset-x-1 h-8 border-2 border-ink bg-coral text-white flex items-stretch shadow-brutalSm"
            data-testid={`compact-tile-stepper-${id}`}
          >
            <button type="button" onClick={() => onUnits(id, units - 1)} aria-label={`Remove one ${product.title}`}
              className="w-8 flex-shrink-0 inline-flex items-center justify-center" data-testid={`compact-tile-dec-${id}`}>
              <Minus className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
            <span className="flex-1 min-w-0 inline-flex items-center justify-center text-xs font-black tabular-nums" data-testid={`compact-tile-units-${id}`}>
              {units}
            </span>
            <button type="button" onClick={() => onUnits(id, units + 1)} aria-label={`Add one more ${product.title}`}
              className="w-8 flex-shrink-0 inline-flex items-center justify-center" data-testid={`compact-tile-inc-${id}`}>
              <Plus className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
          </div>
        ) : a.mode !== "none" && (
          <button
            type="button"
            disabled={a.mode === "blocked"}
            onClick={() => (a.mode === "cart" ? onAdd(product) : a.mode === "direct" ? onBuy(product) : onExternal(product))}
            aria-label={`${a.label} ${product.title}`}
            data-testid={`compact-tile-action-${id}`}
            className={`absolute bottom-1 right-1 h-8 max-w-[calc(100%-0.5rem)] px-2 border-2 border-ink text-[11px] font-black uppercase inline-flex items-center gap-1 shadow-brutalSm ${
              a.mode === "blocked" ? "bg-cream text-ink/50" : "bg-white text-ink"
            }`}
          >
            <span className="truncate">{a.label}</span>
            {a.mode === "external" && <ExternalLink className="w-3 h-3 flex-shrink-0" strokeWidth={2.5} />}
          </button>
        )}
      </div>

      <div className="mt-1.5 min-w-0">
        {a.hasPrice && (
          <p className="flex flex-wrap items-baseline gap-x-1.5 leading-tight" data-testid={`compact-tile-price-${id}`}>
            <span className="font-display font-black text-sm sm:text-base tabular-nums">
              {tiers != null ? <>from {a.sym}{money(tiers)}</> : <>{a.sym}{money(price)}</>}
            </span>
            {tiers == null && mrp > price && (
              <span className="text-[10px] sm:text-xs text-ink/50 line-through tabular-nums" data-testid={`compact-tile-mrp-${id}`}>
                {a.sym}{money(mrp)}
              </span>
            )}
          </p>
        )}
        <button
          type="button"
          onClick={() => onOpen(product)}
          data-testid={`compact-tile-title-${id}`}
          className="mt-0.5 w-full text-left text-[11px] sm:text-xs font-semibold leading-snug"
        >
          <span className="line-clamp-2 break-words">{product.title}</span>
        </button>
        {note && (
          <p className="mt-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-ink/60 truncate" data-testid={`compact-tile-note-${id}`}>
            {note}
          </p>
        )}
      </div>
    </li>
  );
});
